import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from './firebase';

// Base URL for Google Calendar API
const API_BASE_URL = 'https://www.googleapis.com/calendar/v3';

// Exchange authorization code for tokens
export async function exchangeCodeForTokens(code, redirectUri) {
  try {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const clientSecret = process.env.REACT_APP_GOOGLE_CLIENT_SECRET;
    
    if (!clientId || !clientSecret) {
      throw new Error('Google OAuth credentials are not configured');
    }
    
    console.log('Exchanging code for tokens with redirect URI:', redirectUri);
    
    // Make request to Google's token endpoint
    const tokenResponse = await fetch('https://oauth2.googleapis.com/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        code,
        client_id: clientId,
        client_secret: clientSecret,
        redirect_uri: redirectUri,
        grant_type: 'authorization_code',
      }),
    });
    
    console.log('Token response status:', tokenResponse.status);
    
    if (!tokenResponse.ok) {
      let errorMessage = 'Failed to exchange code for tokens';
      try {
        const errorData = await tokenResponse.json();
        errorMessage = `${errorMessage}: ${errorData.error_description || errorData.error || 'Unknown error'}`;
        console.error('Token response error:', errorData);
      } catch (parseError) {
        console.error('Error parsing error response:', parseError);
      }
      throw new Error(errorMessage);
    }
    
    const tokens = await tokenResponse.json();
    console.log('Successfully exchanged code for tokens');
    
    // Add timestamp for token expiration tracking
    tokens.created_at = Date.now();
    
    return tokens;
  } catch (error) {
    console.error('Error exchanging code for tokens:', error);
    throw error;
  }
}

// Refresh access token when it expires
export async function refreshAccessToken(refreshToken) {
  try {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const clientSecret = process.env.REACT_APP_GOOGLE_CLIENT_SECRET;
    
    if (!clientId || !clientSecret) {
      throw new Error('Google OAuth credentials are not configured');
    }
    
    // Make request to Google's token endpoint for refresh
    const tokenResponse = await fetch('https://oauth2.googleapis.com/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        client_id: clientId,
        client_secret: clientSecret,
        refresh_token: refreshToken,
        grant_type: 'refresh_token',
      }),
    });
    
    if (!tokenResponse.ok) {
      const errorData = await tokenResponse.json();
      throw new Error(`Failed to refresh token: ${errorData.error_description || errorData.error || 'Unknown error'}`);
    }
    
    const tokens = await tokenResponse.json();
    
    // Update timestamp and keep the refresh token
    tokens.created_at = Date.now();
    tokens.refresh_token = refreshToken; // Keep the refresh token as Google doesn't return it again
    
    return tokens;
  } catch (error) {
    console.error('Error refreshing access token:', error);
    throw error;
  }
}

// Save calendar tokens to the company profile
export async function saveCalendarTokens(companyId, tokens) {
  try {
    if (!companyId) {
      throw new Error('No company ID provided');
    }
    
    const companyRef = doc(db, "companies", companyId);
    await updateDoc(companyRef, {
      googleCalendar: tokens
    });
    
    return true;
  } catch (error) {
    console.error('Error saving calendar tokens:', error);
    throw error;
  }
}

// Get tokens and refresh if needed
async function getValidTokens(companyId) {
  try {
    if (!companyId) {
      throw new Error('No company ID provided');
    }
    
    const companyRef = doc(db, "companies", companyId);
    const companySnap = await getDoc(companyRef);
    
    if (!companySnap.exists()) {
      throw new Error('Company profile not found');
    }
    
    const data = companySnap.data();
    if (!data.googleCalendar) {
      throw new Error('Google Calendar not connected');
    }
    
    const tokens = data.googleCalendar;
    
    // Check if access token is expired (tokens typically last 1 hour = 3600 seconds)
    const now = Date.now();
    const tokenCreatedAt = tokens.created_at || 0;
    const expiresIn = tokens.expires_in || 3600;
    const expiresAt = tokenCreatedAt + (expiresIn * 1000);
    
    // If token is expired or expiring soon (within 5 minutes), refresh it
    if (now >= expiresAt - 5 * 60 * 1000) {
      console.log('Access token expired or expiring soon, refreshing...');
      
      // Refresh the token
      const newTokens = await refreshAccessToken(tokens.refresh_token);
      
      // Save the new tokens
      await saveCalendarTokens(companyId, newTokens);
      
      return newTokens;
    }
    
    return tokens;
  } catch (error) {
    console.error('Error getting valid tokens:', error);
    throw error;
  }
}

// Fetch calendar events
export async function fetchCalendarEvents(companyId, params = {}) {
  try {
    const tokens = await getValidTokens(companyId);
    
    // Default parameters for listing events
    const defaultParams = {
      maxResults: 10,
      orderBy: 'startTime',
      singleEvents: true,
      timeMin: new Date().toISOString(),
    };
    
    // Merge default params with any custom params
    const queryParams = new URLSearchParams({
      ...defaultParams,
      ...params,
    });
    
    const url = `${API_BASE_URL}/calendars/primary/events?${queryParams}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${tokens.access_token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Failed to fetch calendar events: ${errorData.error?.message || 'Unknown error'}`);
    }
    
    const data = await response.json();
    return data.items || [];
  } catch (error) {
    console.error('Error fetching calendar events:', error);
    throw error;
  }
}

// Helper function to find next events that match a specific search term
export async function findNextEventsByQuery(companyId, query, maxResults = 5) {
  try {
    // Convert query to lowercase for case-insensitive comparison
    const lowercaseQuery = query.toLowerCase();
    
    // Fetch upcoming events
    const events = await fetchCalendarEvents(companyId, {
      maxResults: 25, // Fetch more events to filter through
      timeMin: new Date().toISOString(),
      orderBy: 'startTime',
      singleEvents: true,
    });
    
    // Filter events that match the query in title or description
    const matchingEvents = events.filter(event => {
      const title = (event.summary || '').toLowerCase();
      const description = (event.description || '').toLowerCase();
      return title.includes(lowercaseQuery) || description.includes(lowercaseQuery);
    });
    
    // Return limited number of matching events
    return matchingEvents.slice(0, maxResults);
  } catch (error) {
    console.error('Error finding events by query:', error);
    throw error;
  }
}

// Find the next Level 10 meeting
export async function findNextLevel10Meeting(companyId) {
  try {
    // Specifically search for Level 10 or Level10 meetings
    const level10Events = await findNextEventsByQuery(companyId, 'level 10', 1);
    
    // If none found with 'level 10', try with 'level10' (no space)
    if (level10Events.length === 0) {
      const level10NoSpaceEvents = await findNextEventsByQuery(companyId, 'level10', 1);
      return level10NoSpaceEvents.length > 0 ? level10NoSpaceEvents[0] : null;
    }
    
    return level10Events.length > 0 ? level10Events[0] : null;
  } catch (error) {
    console.error('Error finding next Level 10 meeting:', error);
    throw error;
  }
}

// Format calendar event for display
export function formatCalendarEvent(event) {
  try {
    if (!event) return null;
    
    const summary = event.summary || 'Untitled Event';
    
    // Extract start time
    let startTime = 'Time not specified';
    let endTime = '';
    let formattedDate = '';
    
    if (event.start) {
      if (event.start.dateTime) {
        // Event has a specific time
        const startDate = new Date(event.start.dateTime);
        const endDate = event.end?.dateTime ? new Date(event.end.dateTime) : null;
        
        // Format the date
        formattedDate = startDate.toLocaleDateString(undefined, {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
        
        // Format the time
        startTime = startDate.toLocaleTimeString(undefined, {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        
        if (endDate) {
          endTime = endDate.toLocaleTimeString(undefined, {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          });
        }
      } else if (event.start.date) {
        // All-day event
        const startDate = new Date(event.start.date);
        formattedDate = startDate.toLocaleDateString(undefined, {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
        startTime = 'All day';
        endTime = '';
      }
    }
    
    // Get location if available
    const location = event.location || 'No location specified';
    
    // Get attendees if available
    const attendees = event.attendees 
      ? event.attendees.map(attendee => attendee.email).join(', ')
      : 'No attendees specified';
    
    return {
      id: event.id,
      summary,
      date: formattedDate,
      startTime,
      endTime,
      location,
      attendees,
      htmlLink: event.htmlLink,
      description: event.description || '',
      formattedTimeRange: endTime ? `${startTime} - ${endTime}` : startTime
    };
  } catch (error) {
    console.error('Error formatting calendar event:', error);
    return null;
  }
} 